import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchAKSClusters, setSelectedAKSCluster } from '../../store/slices/monitoringSlice';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

const AKSClusterSelector: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { aksClusters, selectedAKSCluster, loading } = useSelector((state: RootState) => state.monitoring);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Fetch available AKS clusters
        dispatch(fetchAKSClusters());

        // Load previously selected cluster from localStorage, or default to null (All Clusters)
        const stored = localStorage.getItem('selectedAKSCluster');
        if (stored && stored !== 'null') {
            try {
                dispatch(setSelectedAKSCluster(JSON.parse(stored)));
            } catch (error) {
                // If parsing fails, default to All Clusters
                dispatch(setSelectedAKSCluster(null));
            }
        } else {
            // Default to All Clusters
            dispatch(setSelectedAKSCluster(null));
        }
    }, [dispatch]);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectCluster = (cluster: typeof aksClusters[0]|null) => {
        dispatch(setSelectedAKSCluster(cluster));
        setIsOpen(false);
    };

    if (loading) {
        return (
            <div className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                Loading clusters...
            </div>
        );
    }

    if (aksClusters.length === 0) {
        return null;
    }
    console.log("AKS Clusters:", aksClusters); 
    return (
        <div className="px-2 py-2" ref={dropdownRef}>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                AKS Cluster
            </div>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
                <div className="flex items-center space-x-2 overflow-hidden">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedAKSCluster
                            ? (selectedAKSCluster.healthStatus === 'Healthy' ? 'bg-green-500' : 'bg-yellow-500')
                            : 'bg-gray-400'
                        }`} />
                    <span className="truncate">
                        {selectedAKSCluster?.name || 'All Clusters'}
                    </span>
                </div>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-60 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
                    {/* All Clusters Option */}
                    <button
                        onClick={() => handleSelectCluster(null)}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between group border-b border-gray-200 dark:border-gray-700 ${selectedAKSCluster === null ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                            }`}
                    >
                        <div className="flex items-center space-x-2 overflow-hidden flex-1">
                            <div className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-400 dark:bg-gray-500" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    All Clusters
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    View all AKS clusters
                                </div>
                            </div>
                        </div>
                        {selectedAKSCluster === null && (
                            <CheckIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        )}
                    </button>

                    {/* Individual Cluster Options */}
                    {/* Individual Cluster Options */}
                    {aksClusters.map((cluster) => (
                        <button
                            key={cluster.id}
                            onClick={() => handleSelectCluster(cluster)}
                            className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between group ${selectedAKSCluster?.id === cluster.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                }`}
                        >
                            <div className="flex items-center space-x-2 overflow-hidden flex-1">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cluster.healthStatus === 'Healthy'
                                        ? 'bg-green-500'
                                        : 'bg-yellow-500'
                                    }`} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {cluster.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {cluster.resourceGroup} � {cluster.location}
                                    </div>
                                </div>
                            </div>
                            {selectedAKSCluster?.id === cluster.id && (
                                <CheckIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                            )}
                        </button>
                    ))}

                    {aksClusters.length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                            No AKS clusters available
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AKSClusterSelector;